import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";
import { tx, txv } from "../lib/tx";

/**
 * "Read while you wait" summary for the sharing-files start screen. Three short
 * slides (what/who, main risks, key terms) shown in the reader while the 3D
 * room downloads. Content only; the reader UI lives in ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. share dialog: pick the right person, give only the access needed */
/* ------------------------------------------------------------------ */
function ShareDialogArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label={tx("Example: sharing a file")} aria={tx("A share dialog where two people called Morgan appear, one in your organisation and one outside it, with comment-only access chosen")} height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        {tx("Share file")}
      </text>
      <rect x="226" y="38" width="118" height="20" rx="10" fill={C.panel2} stroke={C.line} />
      <text x="285" y="52" fontSize="10.5" fontFamily={MONO} fill={C.soft} textAnchor="middle">
        workshop-plan.pdf
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <text x="16" y="87" fontSize="10.5" fill={C.muted}>
        {tx("Add people")}
      </text>
      <rect x="16.5" y="93.5" width="327" height="30" rx="8" fill={C.code} stroke={C.line} />
      <text x="29" y="113" fontSize="12.5" fill={C.text}>
        {tx("Morgan")}
      </text>
      <rect className="sr-anim-blink" x="74" y="101" width="1.5" height="15" rx="0.75" fill={C.mint} />

      <rect x="16.5" y="132.5" width="327" height="44" rx="8" fill={C.mint} fillOpacity="0.08" stroke={C.mint} strokeOpacity="0.45" />
      <circle cx="38" cy="154.5" r="12" fill={C.panel2} stroke={C.line} />
      <text x="38" y="159" fontSize="12" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("M")}
      </text>
      <text x="58" y="151" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Morgan Reyes")}
      </text>
      <text x="58" y="166" fontSize="10.5" fontFamily={MONO} fill={C.muted}>
        morgan.reyes@yourcompany.com
      </text>
      <circle className="sr-anim-ring" cx="324" cy="154.5" r="7" fill="none" stroke={C.mint} strokeWidth="1.5" />
      <circle cx="324" cy="154.5" r="7" fill={C.mint} fillOpacity="0.16" />
      <polyline points="320.5,154.5 323,157.2 327.8,151.6" fill="none" stroke={C.mint} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />

      <rect x="16.5" y="184.5" width="327" height="44" rx="8" fill={C.panel2} stroke={C.line} />
      <circle cx="38" cy="206.5" r="12" fill={C.panel} stroke={C.line} />
      <text x="38" y="211" fontSize="12" fontWeight="700" fill={C.muted} textAnchor="middle">
        {tx("M")}
      </text>
      <text x="58" y="203" fontSize="12.5" fontWeight="700" fill={C.soft}>
        {tx("Morgan Reyes")}
      </text>
      <text x="58" y="218" fontSize="10.5" fontFamily={MONO} fill={C.muted}>
        m.reyes@othercorp.example
      </text>
      <rect x="276" y="196" width="56" height="20" rx="10" fill={C.amber} fillOpacity="0.12" stroke={C.amber} strokeOpacity="0.45" />
      <text x="304" y="210" fontSize="10.5" fontWeight="700" fill={C.amber} textAnchor="middle">
        {tx("External")}
      </text>

      <rect x="16.5" y="241.5" width="327" height="38" rx="8" fill={C.code} stroke={C.line} />
      <text x="29" y="264" fontSize="11.5" fill={C.muted}>
        {tx("Access")}
      </text>
      <rect x="78" y="249" width="108" height="22" rx="11" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.45" />
      <text x="132" y="264" fontSize="11.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("Can comment")}
      </text>
      <text x="330" y="264" fontSize="10.5" fill={C.muted} textAnchor="end">
        {tx("Only what the task needs")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. access list: inherited access, a public link and a leftover guest */
/* ------------------------------------------------------------------ */
function AccessRow({ y, name, role, tone, note }: { y: number; name: string; role: string; tone: string; note?: string }): JSX.Element {
  return (
    <g>
      <circle cx="30" cy={y} r="11" fill={tone} fillOpacity="0.12" stroke={tone} strokeOpacity="0.5" />
      <circle cx="30" cy={y - 2.6} r="2.8" fill="none" stroke={tone} strokeWidth="1.4" />
      <path d={`M24.2 ${y + 6} a5.8 4.6 0 0 1 11.6 0`} fill="none" stroke={tone} strokeWidth="1.4" strokeLinecap="round" />
      <text x="50" y={note ? y - 1 : y + 4.2} fontSize="12.5" fontWeight="600" fill={C.text}>
        {txv(name)}
      </text>
      {note ? (
        <text x="50" y={y + 12} fontSize="10.5" fill={C.muted}>
          {txv(note)}
        </text>
      ) : null}
      <text x="334" y={y + 4.2} fontSize="11.5" fill={C.soft} textAnchor="end">
        {txv(role)}
      </text>
    </g>
  );
}

function AccessArt(): JSX.Element {
  const H = 300;
  return (
    <Frame label={tx("Example: who can open this file")} aria={tx("An access list for one file: the project team inherited from a folder, anyone with the link, and a former teammate who still has access")} height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        {tx("Access")}
      </text>
      <text x="104" y="53" fontSize="11" fontFamily={MONO} fill={C.muted}>
        budget.xlsx
      </text>
      <rect className="sr-anim-float" x="262" y="38" width="82" height="22" rx="11" fill={C.panel2} stroke={C.line} />
      <text x="303" y="53" fontSize="10.5" fontWeight="700" fill={C.soft} textAnchor="middle">
        {tx("4 ways in")}
      </text>
      <line x1="16" y1="74.5" x2="344" y2="74.5" stroke={C.line} />

      <AccessRow y={98} name="Project team" role="Can edit" tone={C.mint} note={tx("Inherited from the shared folder")} />
      <line x1="16" y1="122.5" x2="344" y2="122.5" stroke={C.line} strokeOpacity="0.6" />
      <AccessRow y={146} name="Partner company" role="Can comment" tone={C.mint} note={tx("Invited for this task")} />
      <line x1="16" y1="170.5" x2="344" y2="170.5" stroke={C.line} strokeOpacity="0.6" />

      <rect x="16.5" y="178.5" width="327" height="44" rx="8" fill={C.amber} fillOpacity="0.08" stroke={C.amber} strokeOpacity="0.4" />
      <AccessRow y={200} name="Anyone with the link" role="Can view" tone={C.amber} />

      <rect x="16.5" y="230.5" width="327" height="54" rx="8" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.4" />
      <rect x="16" y="236" width="3" height="43" rx="1.5" fill={C.red} />
      <circle className="sr-anim-ring" cx="30" cy="251" r="11" fill="none" stroke={C.red} strokeWidth="1.5" />
      <AccessRow y={251} name="Former teammate" role="Can edit" tone={C.red} />
      <text x="50" y="272" fontSize="10.5" fontWeight="600" fill={C.red}>
        {tx("Left the project, still has access")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. glossary: grid of icon chips                                     */
/* ------------------------------------------------------------------ */
type Glyph = "level" | "inherit" | "link" | "guest" | "app" | "revoke" | "least";

function GlyphShape({ g }: { g: Glyph }): JSX.Element {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "level":
      return (
        <g {...s}>
          <rect x="-6.5" y="-1.5" width="13" height="9" rx="2" />
          <path d="M-3.5 -1.5 V-4.5 a3.5 3.5 0 0 1 7 0 V-1.5" />
        </g>
      );
    case "inherit":
      return (
        <g {...s}>
          <path d="M-9 -6 h6 l2 2.4 h8 v9.6 h-16 z" />
          <polyline points="-1,-0.5 -1,3 3,3" />
          <polyline points="1,1 3,3 1,5" />
        </g>
      );
    case "link":
      return (
        <g {...s}>
          <circle cx="-3.8" cy="0" r="4.2" />
          <circle cx="3.8" cy="0" r="4.2" />
        </g>
      );
    case "guest":
      return (
        <g {...s}>
          <circle cx="0" cy="-3.6" r="3.6" />
          <path d="M-7 8 a7 5.6 0 0 1 14 0" />
        </g>
      );
    case "app":
      return (
        <g {...s}>
          <rect x="-8" y="-8" width="6.4" height="6.4" rx="1.4" />
          <rect x="1.6" y="-8" width="6.4" height="6.4" rx="1.4" />
          <rect x="-8" y="1.6" width="6.4" height="6.4" rx="1.4" />
          <rect x="1.6" y="1.6" width="6.4" height="6.4" rx="1.4" />
        </g>
      );
    case "revoke":
      return (
        <g {...s}>
          <circle cx="0" cy="0" r="7.5" />
          <line x1="-5.3" y1="5.3" x2="5.3" y2="-5.3" />
        </g>
      );
    case "least":
      return (
        <g {...s}>
          <line x1="-8" y1="-4" x2="8" y2="-4" />
          <line x1="-8" y1="4" x2="8" y2="4" />
          <circle cx="-2.5" cy="-4" r="2.3" fill={C.panel} />
          <circle cx="3.5" cy="4" r="2.3" fill={C.panel} />
        </g>
      );
  }
}

interface ChipSpec {
  g: Glyph;
  title: string;
  sub: string;
}

const CHIPS: ChipSpec[] = [
  { g: "level", title: "Access level", sub: "view, comment, edit" },
  { g: "inherit", title: "Inherited", sub: "comes from the folder" },
  { g: "link", title: "Public link", sub: "anyone who has it" },
  { g: "guest", title: "Guest", sub: "person outside" },
  { g: "app", title: "Connected app", sub: "acts for you" },
  { g: "revoke", title: "Revoke", sub: "take access away" },
  { g: "least", title: "Least access", sub: "only what the task needs" },
];

function TermsArt(): JSX.Element {
  const H = 298;
  return (
    <Frame label={tx("Terms at a glance")} aria={tx("Seven terms shown as labelled icon chips: access level, inherited access, public link, guest, connected app, revoke and least access")} height={H}>
      {CHIPS.map((c, i) => {
        const last = i === CHIPS.length - 1;
        const col = i % 2;
        const row = Math.floor(i / 2);
        const x = last ? 16 : 16 + col * 172;
        const y = 38 + row * 64;
        const w = last ? 328 : 156;
        return (
          <g key={c.g} transform={`translate(${x} ${y})`}>
            <rect x="0.5" y="0.5" width={w - 1} height="51" rx="10" fill={C.panel2} stroke={C.line} />
            <circle className={last ? "sr-anim-pulse" : undefined} cx="26" cy="26" r="16" fill={C.mint} fillOpacity="0.12" />
            <g transform="translate(26 26)">
              <GlyphShape g={c.g} />
            </g>
            <text x="51" y="23" fontSize="12.5" fontWeight="700" fill={C.text}>
              {txv(c.title)}
            </text>
            <text x="51" y="39" fontSize="10.5" fill={C.muted}>
              {txv(c.sub)}
            </text>
          </g>
        );
      })}
    </Frame>
  );
}

export const SHARING_FILES_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "Share the right file, with the right people.",
    illustration: ShareDialogArt,
    caption: "Check who it is. Choose what they can do.",
    sections: [
      {
        h: "What this is about",
        body: "A film, then a computer where a small team asks you for files. You check each file, choose who gets it and what they can do, then answer six questions.",
      },
      {
        h: "Who it\u2019s for",
        body: "Everyone who sends, shares or collaborates on files at work. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "A share button will send anything to anyone. It never asks if the file is ready, if the person is the right one, or if they need to edit. You are the check, every time.",
      },
    ],
  },
  {
    tab: "Main risks",
    headline: "Where a quick share goes wrong.",
    illustration: AccessArt,
    caption: "Every way in is a decision someone made.",
    sections: [
      {
        h: "The wrong person gets it",
        body: "Two colleagues can share a name. Check the full address and the organisation before you press send.",
      },
      {
        h: "Too much access, or the wrong version",
        body: "Internal drafts and comments stay internal. Share the approved copy, and give view or comment unless editing is needed.",
      },
      {
        h: "Access you can\u2019t see",
        body: "A file inherits the access of its folder. Someone who left the project may still be able to open it.",
      },
      {
        h: "Links, apps and loose ends",
        body: "A link opens for anyone who has it. An app you approve keeps its access after a password change. Remove access when the work is done.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: TermsArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "Access level", body: "What a person can do with a file: view it, comment on it, or edit it." },
      { h: "Inherited access", body: "A file gets the access of the folder it sits in, on top of any invitation." },
      { h: "Public link", body: "A link that opens the file for anyone who has it, signed in or not." },
      { h: "Guest", body: "A person from outside your organisation who has been given access." },
      { h: "Connected app", body: "A third-party app you allow to read files or mail. A new password does not remove it." },
      { h: "Revoke", body: "Take access away once the work no longer needs it." },
      { h: "Least access", body: "Give each person only the access the task needs, and no more." },
    ],
  },
];
